import React from 'react';
import {
  Modal,
  Typography,
  Form,
  Input,
  Button,
  PhoneNumber,
  useToast,
  Select,
} from '@crisellevaldez/speculo-ui';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  createOrganizationSchema,
  CreateOrganizationFormData,
} from '../../../schemas/organizations/createOrganization.schema';
import { useCreateOrganization } from '../../../hooks/organizations/useCreateOrganization';
import { TIMEZONE_OPTIONS } from '../../../constants/timezones';

interface CreateOrganizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (orgId: string) => void;
}

export const CreateOrganizationModal: React.FC<CreateOrganizationModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { showToast } = useToast();
  const { createOrg, isPending } = useCreateOrganization();

  const {
    handleSubmit,
    reset,
    register,
    setValue,
    control,
    formState: { errors },
  } = useForm<CreateOrganizationFormData>({
    resolver: zodResolver(createOrganizationSchema),
    defaultValues: {
      org_name: '',
      org_phone: '',
      email: '',
      sync_link: '',
      notification_email: '',
      client_address: {
        street: '',
        city: '',
        state: '',
        zip: '',
      },
      timezone: '',
      activeClient: true,
      active_subscription: false,
      accepted_terms: true,
      is_onboarding_complete: false,
      primary_contact: {
        name: '',
        title: '',
        email: '',
        phone: '',
      },
    },
  });

  const handleOrgPhoneChange = (value: string | undefined) => {
    setValue('org_phone', value || '');
  };

  const handlePrimaryContactPhoneChange = (value: string | undefined) => {
    setValue('primary_contact.phone', value || '');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: CreateOrganizationFormData) => {
    try {
      const orgId = await createOrg({ ...data, is_onboarding_complete: false });

      showToast({
        message: 'Organization created! You can now select the organization and finish the setup.',
        variant: 'success',
      });

      reset();
      onSuccess?.(orgId);
      onClose();
    } catch (error) {
      showToast({
        message: error instanceof Error ? error.message : 'Failed to create organization',
        variant: 'error',
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="2xl">
      <Modal.Header>
        <Typography variant="h3">Create New Organization</Typography>
      </Modal.Header>
      <Modal.Body>
        <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
          {/* Organization Information */}
          <div className="space-y-4">
            <Typography variant="h3" className="text-sm font-semibold">
              Organization Information
            </Typography>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Control required>
                <Form.Label>Organization Name</Form.Label>
                <Input {...register('org_name')} placeholder="Enter organization name" error={errors.org_name?.message} />
              </Form.Control>
              <Form.Control required>
                <Form.Label>Organization Phone</Form.Label>
                <PhoneNumber
                  value=""
                  onChange={handleOrgPhoneChange}
                  placeholder="Enter phone number"
                  defaultCountry="US"
                  error={errors.org_phone?.message}
                />
              </Form.Control>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Control required>
                <Form.Label>Email</Form.Label>
                <Input {...register('email')} type="email" placeholder="organization@example.com" error={errors.email?.message} />
              </Form.Control>
              <Form.Control required>
                <Form.Label>Email For Answer Notifications</Form.Label>
                <Input {...register('notification_email')} type="email" placeholder="notifications@example.com" error={errors.notification_email?.message} />
              </Form.Control>
            </div>
            <Form.Control>
              <Form.Label>Sync Link</Form.Label>
              <Input {...register('sync_link')} placeholder="Enter sync link (optional)" error={errors.sync_link?.message} />
            </Form.Control>
          </div>

          {/* Organization Address */}
          <div className="space-y-4">
            <Typography variant="h3" className="text-sm font-semibold">
              Organization Address
            </Typography>
            <Form.Control required>
              <Form.Label>Street</Form.Label>
              <Input {...register('client_address.street')} placeholder="Enter street address" error={errors.client_address?.street?.message} />
            </Form.Control>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Form.Control required>
                <Form.Label>City</Form.Label>
                <Input {...register('client_address.city')} placeholder="Enter city" error={errors.client_address?.city?.message} />
              </Form.Control>
              <Form.Control required>
                <Form.Label>State (2 letters)</Form.Label>
                <Input {...register('client_address.state')} placeholder="CA" maxLength={2} error={errors.client_address?.state?.message} />
              </Form.Control>
              <Form.Control required>
                <Form.Label>Zip Code</Form.Label>
                <Input {...register('client_address.zip')} placeholder="12345" error={errors.client_address?.zip?.message} />
              </Form.Control>
            </div>
            <Form.Control required>
              <Form.Label>Timezone</Form.Label>
              <Controller
                control={control}
                name="timezone"
                render={({ field }) => (
                  <Select
                    {...field}
                    error={errors.timezone?.message}
                    options={TIMEZONE_OPTIONS}
                    placeholder="Select timezone"
                    searchable
                  />
                )}
              />
            </Form.Control>
          </div>

          {/* Primary Contact */}
          <div className="space-y-4">
            <Typography variant="h3" className="text-sm font-semibold">
              Primary Contact
            </Typography>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Control required>
                <Form.Label>Name</Form.Label>
                <Input {...register('primary_contact.name')} placeholder="Enter contact name" error={errors.primary_contact?.name?.message} />
              </Form.Control>
              <Form.Control required>
                <Form.Label>Title</Form.Label>
                <Input {...register('primary_contact.title')} placeholder="Enter title" error={errors.primary_contact?.title?.message} />
              </Form.Control>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Control required>
                <Form.Label>Email</Form.Label>
                <Input {...register('primary_contact.email')} type="email" placeholder="contact@example.com" error={errors.primary_contact?.email?.message} />
              </Form.Control>
              <Form.Control required>
                <Form.Label>Phone</Form.Label>
                <PhoneNumber
                  value=""
                  onChange={handlePrimaryContactPhoneChange}
                  placeholder="Enter phone number"
                  defaultCountry="US"
                  error={errors.primary_contact?.phone?.message}
                />
              </Form.Control>
            </div>
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} type="button">
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit(onSubmit)}
          isLoading={isPending}
          disabled={isPending}
        >
          Create Organization
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
